import { Grid, Tab, Tabs, makeStyles } from '@material-ui/core';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useAction } from 'core/store/store';

import {
    makeUserMenuStoreInfoFetchAction,
    makeUserMenuStoreInfoClearAction,
    makeUserMenuStoreInfoUpdateAction,
} from 'state/userMenu/actions';
import { ShopTabs, StoreVendorDetailsEditForm } from 'forms/StoreVendorDetailsForm/StoreVendorDetailsForm';

import { ReactComponent as BackIcon } from 'assets/icons/main-24/back-o--24.svg';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { Icon } from 'components/Icon/Icon';
import { Text, TextTypography } from 'components/Text/Text';
import { LayoutFixedItem } from 'components/Layout/LayoutFixedItem';
import { LayoutFullHeightContainer } from 'components/Layout/LayoutFullHeightContainer';

import i18n from '../../../../i18n';
import { RoutePath } from 'RoutePath';
import { StoreVendorSettingsEditForm } from '../../Registration/SettingsForm';
import {
    makeRegistrationStoreSettingsFetchAction,
    makeRegistrationSubmitStoreSettingsAction,
} from 'state/registration/actions';
import { subscriptionBannerSelector } from '../../../state/subscription/selectors';
import useScrollPosition from '@react-hook/window-scroll';
import { SupportedCountryCodes } from '../../../types';
import { authIsoCodeSelector } from 'state/auth/selectors';

const DetailsForm: any = StoreVendorDetailsEditForm;
const SettingsForm: any = StoreVendorSettingsEditForm;

const useStyles = makeStyles(theme => ({
    editHeader: {
        position: 'fixed',
        backgroundColor: '#fafafa',
        top: 0,
        width: '100%',
        display: 'flex',
        padding: theme.spacing(2.5),
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
        zIndex: 2, //check vendor menu for desktop
        [theme.breakpoints.up(640)]: {
            display: 'none',
        },
        [theme.breakpoints.down(375)]: {
            padding: theme.spacing(2),
        },
    },
    iconClickableContainer: {
        cursor: 'pointer',
    },
    editTitle: {
        paddingRight: theme.spacing(3),
        width: '100%',
        textAlign: 'center',
    },
    tabsContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'fixed',
        top: ({ isBannerHidden }: { country: string; isBannerHidden: boolean }) => (isBannerHidden ? undefined : 110),
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
        zIndex: 3,
        [theme.breakpoints.down(640)]: {
            top: 60,
        },
        [theme.breakpoints.down(375)]: {
            top: 40,
        },
    },
    tabs: {
        width: '100%',
        backgroundColor: '#fafafa',
        paddingLeft: theme.spacing(30),

        [theme.breakpoints.down(1280)]: {
            paddingLeft: 80,
        },
        [theme.breakpoints.down(960)]: {
            paddingLeft: 40,
        },
        [theme.breakpoints.down(640)]: {
            paddingLeft: 24,
        },
        [theme.breakpoints.down(480)]: {
            paddingLeft: 16,
        },
    },
    tabsItem: {
        minWidth: 0,
        padding: 0,
        marginRight: 32,
        '& span': {
            paddingTop: 16,
            paddingBottom: 16,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: 800,
        margin: 'auto',
        marginTop: theme.spacing(7),
        [theme.breakpoints.down(960)]: {
            padding: theme.spacing(0, 5),
            // paddingLeft: 40,
        },
        [theme.breakpoints.down(640)]: {
            padding: theme.spacing(0, 3),
            marginTop: theme.spacing(14),
        },
        [theme.breakpoints.down(768)]: {
            // paddingBottom: ({ isCreateMode }: { isCreateMode: boolean }) => !isCreateMode && 120,
            paddingBottom: 120,
        },
        [theme.breakpoints.down(480)]: {
            padding: 0,
        },
        // paddingBottom: ({ isCreateMode }: { isCreateMode: boolean }) => !isCreateMode && 130,
        paddingBottom: 130,
    },
}));

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div hidden={value !== index} {...other}>
            {value === index && children}
        </div>
    );
}

export const VendorStoreInfo: FC<{ isBannerHidden: boolean }> = ({ isBannerHidden }) => {
    const action = useAction();
    const dispatch = useDispatch();
    const hideBanner = useSelector(subscriptionBannerSelector);
    const scrollY = useScrollPosition(60 /*fps*/);
    const country = useSelector(authIsoCodeSelector) || SupportedCountryCodes.RUS;
    const classes = useStyles({ hideBanner, scrollY, isBannerHidden });

    useEffect(() => {
        dispatch(makeUserMenuStoreInfoFetchAction());
        dispatch(makeRegistrationStoreSettingsFetchAction());
        return () => {
            dispatch(makeUserMenuStoreInfoClearAction());
        };
    }, []);

    const [imageCrop, setImageCrop] = useState(null);

    const handleSubmit = useCallback(
        (data: any) => {
            action(makeUserMenuStoreInfoUpdateAction, { ...data, imageCrop });
        },
        [action, imageCrop],
    );

    const [value, setValue] = useState(ShopTabs.SHOP);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: ShopTabs) => {
        setValue(newValue);
    };

    const { t } = i18n.useTranslation();

    const history = useHistory();
    const goToVendorDashboard = useCallback(() => history.push(RoutePath.VENDOR_DASHBOARD), [history]);

    const handleSecondFormSubmit = useCallback(
        (data: any) => {
            action(makeRegistrationSubmitStoreSettingsAction, { ...data });
        },
        [action],
    );

    return (
        <>
            <div className={classes.editHeader}>
                <div className={classes.iconClickableContainer} onClick={goToVendorDashboard}>
                    <Icon component={BackIcon} size={24} svgSize={24} />
                </div>
                <div className={classes.editTitle}>
                    <Text typography={TextTypography.LEAD_ACCENT}>{t('onboarding:store.settings')}</Text>
                </div>
            </div>
            <LayoutFullHeightContainer>
                <LayoutFixedItem>
                    {country === SupportedCountryCodes.RUS && (
                        <div className={classes.tabsContainer} color="default">
                            <Tabs
                                value={value}
                                indicatorColor="primary"
                                textColor="primary"
                                className={classes.tabs}
                                onChange={handleChange}
                            >
                                <Tab
                                    className={classes.tabsItem}
                                    label={
                                        <Text typography={TextTypography.BODY_HEADING}>
                                            {t('onboarding:storeDetails.tabs.shop')}
                                        </Text>
                                    }
                                    value={ShopTabs.SHOP}
                                />
                                <Tab
                                    className={classes.tabsItem}
                                    label={
                                        <Text typography={TextTypography.BODY_HEADING}>
                                            {t('onboarding:storeDetails.tabs.payDelivery')}
                                        </Text>
                                    }
                                    value={ShopTabs.PAY_DELIVERY}
                                />
                            </Tabs>
                        </div>
                    )}

                    <HorizontalDivider />

                    {country === SupportedCountryCodes.RUS ? (
                        <>
                            <TabPanel value={value} index={ShopTabs.SHOP}>
                                <DetailsForm onChangeImageCrop={setImageCrop} onSubmit={handleSubmit} />;
                            </TabPanel>

                            <TabPanel value={value} index={ShopTabs.PAY_DELIVERY}>
                                <Grid container justify="center" className={classes.content}>
                                    <SettingsForm
                                        onSubmit={handleSecondFormSubmit}
                                        submitButtonText={'OK'}
                                        onCardClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS_CARD)}
                                        onCashClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS_CASH)}
                                        onDeliveryClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS_DELIVERY)}
                                        onOnlineClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS_ONLINE)}
                                        onPickupClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS_PICKUP)}
                                        onLoginAsCustomerClick={() => {}}
                                    />
                                </Grid>
                            </TabPanel>
                        </>
                    ) : (
                        <DetailsForm onChangeImageCrop={setImageCrop} onSubmit={handleSubmit} />
                    )}
                </LayoutFixedItem>
            </LayoutFullHeightContainer>
        </>
    );
};
